import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { api, getErrorMessage } from '../App';

const LogoUpload = ({ 
  currentLogo, 
  onLogoChange,
  entityType = 'hotel', // 'hotel' or 'restaurant'
  entityId = null,
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentLogo);
  const fileInputRef = useRef(null);
  
  // Get backend URL for static files (without /api suffix)
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Update preview when currentLogo prop changes
  useEffect(() => {
    if (currentLogo) {
      setPreviewUrl(currentLogo);
    }
  }, [currentLogo]);

  // Build full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url; // Base64 data URL
    if (url.startsWith('http')) return url;  // Already full URL
    // For /api/uploads/ paths, just prepend the backend URL
    // For /uploads/ paths (old format), convert to /api/uploads/
    if (url.startsWith('/uploads/')) {
      url = '/api' + url;
    }
    return `${backendUrl}${url}`;
  };

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      if (entityId) {
        formData.append('entity_id', entityId);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/upload/logo?entity_type=${entityType}${entityId ? `&entity_id=${entityId}` : ''}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewUrl(data.logo_url);
        onLogoChange?.(data.logo_url);
        toast.success('Logo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload logo');
        setPreviewUrl(currentLogo); // Revert preview
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload logo');
      setPreviewUrl(currentLogo); // Revert preview
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onLogoChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-4">
      {/* Preview */}
      <div 
        className={`${sizeClasses[size]} rounded-lg border-2 border-dashed border-gray-300 
                    flex items-center justify-center overflow-hidden bg-gray-50 relative group
                    ${!previewUrl ? 'hover:border-blue-400 hover:bg-blue-50 cursor-pointer' : ''}`}
        onClick={() => !previewUrl && fileInputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        ) : previewUrl ? (
          <>
            <img 
              src={getImageUrl(previewUrl)} 
              alt="Logo preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Failed to load logo:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
                          transition-opacity flex items-center justify-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center p-2">
            <Image className="w-6 h-6 mx-auto text-gray-400" />
            <span className="text-xs text-gray-500 mt-1 block">Click to upload</span>
          </div>
        )}
      </div>

      {/* Upload button (alternative) */}
      {!previewUrl && (
        <div className="flex-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload Logo
          </Button>
          <p className="text-xs text-gray-500 mt-1">
            JPG, PNG, GIF or WebP. Max 5MB.
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="logo-file-input"
      />
    </div>
  );
};

export default LogoUpload;
